// Backtesting Engine - Test strategies on historical data
// Simulates real trading with position sizing, costs, and P&L tracking

export class BacktestingEngine {
  constructor(historicalData) {
    this.data = historicalData;
    this.tradingCosts = {
      commission: 2.5, // ₪ per contract
      slippage: 0.1, // % of price
    };
  }

  /**
   * Backtest a strategy across historical data
   */
  runBacktest(strategy, params = {}) {
    const {
      startDate = this.data[this.data.length - 1].date,
      endDate = this.data[0].date,
      initialCapital = 100000,
      maxRiskPerTrade = 0.02, // 2% of capital
      exitType = 'expiration', // 'expiration' or 'early'
    } = params;

    // Filter data by date range
    const testData = this.data.filter(
      exp => exp.date >= startDate && exp.date <= endDate
    );

    const results = {
      trades: [],
      equity: [{ date: startDate, value: initialCapital }],
      metrics: {},
      monthlyReturns: {},
    };

    let capital = initialCapital;
    let openPositions = [];

    // Simulate trading on each expiration
    for (let i = testData.length - 1; i >= 0; i--) {
      const currentExp = testData[i];
      const nextExp = i > 0 ? testData[i - 1] : null;

      // Close expired positions
      if (openPositions.length > 0) {
        openPositions.forEach(position => {
          const pnl = this.calculatePnL(position, currentExp.price);
          capital += pnl;
          
          results.trades.push({
            ...position,
            exitDate: currentExp.date,
            exitPrice: currentExp.price,
            pnl,
            return: (pnl / position.cost) * 100,
          });
        });
        openPositions = [];
      }

      // Open new position based on strategy
      if (nextExp) {
        const signal = this.evaluateStrategy(strategy, currentExp, testData.slice(i));
        
        if (signal.action !== 'none') {
          const positionSize = this.calculatePositionSize(
            capital,
            maxRiskPerTrade,
            signal.risk
          );

          const position = {
            entryDate: currentExp.date,
            entryPrice: currentExp.price,
            strategy: signal.strategy,
            legs: signal.legs,
            cost: signal.cost * positionSize,
            quantity: positionSize,
            expectedReturn: signal.expectedReturn,
            risk: signal.risk,
          };

          openPositions.push(position);
          capital -= position.cost;
        }
      }

      // Track equity
      results.equity.push({
        date: currentExp.date,
        value: capital + openPositions.reduce((sum, p) => sum + p.cost, 0),
      });
    }

    // Calculate metrics
    results.metrics = this.calculateMetrics(results);
    results.monthlyReturns = this.calculateMonthlyReturns(results);

    return results;
  }

  /**
   * Evaluate strategy and generate trading signal
   */
  evaluateStrategy(strategy, currentExp, recentData) {
    // Calculate recent trend
    const changes = [];
    for (let i = 0; i < Math.min(3, recentData.length - 1); i++) {
      const change = ((recentData[i].price - recentData[i + 1].price) / recentData[i + 1].price) * 100;
      changes.push(change);
    }

    const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
    const trend = avgChange > 0.5 ? 'bullish' : avgChange < -0.5 ? 'bearish' : 'neutral';
    const volatility = currentExp.type === 'חודשי' ? 'high' : 'normal';

    // Strategy: Iron Condor in low volatility
    if (strategy === 'iron_condor' && volatility === 'normal' && trend === 'neutral') {
      const price = currentExp.price;
      return {
        action: 'open',
        strategy: 'Iron Condor',
        legs: [
          { type: 'put', action: 'sell', strike: Math.round(price * 0.97 / 10) * 10, premium: 3 },
          { type: 'put', action: 'buy', strike: Math.round(price * 0.95 / 10) * 10, premium: 1.5 },
          { type: 'call', action: 'sell', strike: Math.round(price * 1.03 / 10) * 10, premium: 3 },
          { type: 'call', action: 'buy', strike: Math.round(price * 1.05 / 10) * 10, premium: 1.5 },
        ],
        cost: 300, // Net credit
        expectedReturn: 0.15, // 15%
        risk: 0.05, // 5%
      };
    }

    // Strategy: Bull Call Spread in bullish trend
    if (strategy === 'bull_call_spread' && trend === 'bullish') {
      const price = currentExp.price;
      return {
        action: 'open',
        strategy: 'Bull Call Spread',
        legs: [
          { type: 'call', action: 'buy', strike: Math.round(price * 1.00 / 10) * 10, premium: 5 },
          { type: 'call', action: 'sell', strike: Math.round(price * 1.05 / 10) * 10, premium: 2 },
        ],
        cost: 150,
        expectedReturn: 0.25,
        risk: 0.08,
      };
    }

    // Strategy: Bear Put Spread in bearish trend
    if (strategy === 'bear_put_spread' && trend === 'bearish') {
      const price = currentExp.price;
      return {
        action: 'open',
        strategy: 'Bear Put Spread',
        legs: [
          { type: 'put', action: 'buy', strike: Math.round(price * 1.00 / 10) * 10, premium: 5 },
          { type: 'put', action: 'sell', strike: Math.round(price * 0.95 / 10) * 10, premium: 2 },
        ],
        cost: 150,
        expectedReturn: 0.25,
        risk: 0.08,
      };
    }

    return { action: 'none' };
  }

  /**
   * Calculate P&L for a position
   */
  calculatePnL(position, exitPrice) {
    let totalPnL = 0;

    position.legs.forEach(leg => {
      let legValue = 0;
      if (leg.type === 'call') {
        legValue = Math.max(0, exitPrice - leg.strike);
      } else {
        legValue = Math.max(0, leg.strike - exitPrice);
      }

      const legPnL = leg.action === 'buy'
        ? (legValue - leg.premium) * 50
        : (leg.premium - legValue) * 50;

      totalPnL += legPnL * position.quantity;
    });

    // Subtract costs
    totalPnL -= this.tradingCosts.commission * position.legs.length * position.quantity;

    return totalPnL;
  }

  /**
   * Calculate position size based on risk management
   */
  calculatePositionSize(capital, maxRiskPerTrade, strategyRisk) {
    const maxRiskAmount = capital * maxRiskPerTrade;
    const riskPerContract = strategyRisk * 1000; // Approximate
    const size = Math.floor(maxRiskAmount / riskPerContract);
    return Math.max(1, Math.min(size, 10)); // Between 1-10 contracts
  }

  /**
   * Calculate performance metrics
   */
  calculateMetrics(results) {
    const { trades, equity } = results;

    if (trades.length === 0) {
      return { totalReturn: 0, winRate: 0, sharpeRatio: 0, maxDrawdown: 0 };
    }

    const totalPnL = trades.reduce((sum, t) => sum + t.pnl, 0);
    const initialCapital = equity[0].value;
    const finalCapital = equity[equity.length - 1].value;
    const totalReturn = ((finalCapital - initialCapital) / initialCapital) * 100;

    const winningTrades = trades.filter(t => t.pnl > 0).length;
    const winRate = (winningTrades / trades.length) * 100;

    // Calculate Sharpe Ratio
    const returns = trades.map(t => t.return);
    const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const stdDev = Math.sqrt(
      returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length
    );
    const sharpeRatio = stdDev > 0 ? (avgReturn / stdDev) : 0;

    // Calculate Max Drawdown
    let maxDrawdown = 0;
    let peak = equity[0].value;
    equity.forEach(point => {
      if (point.value > peak) peak = point.value;
      const drawdown = ((peak - point.value) / peak) * 100;
      if (drawdown > maxDrawdown) maxDrawdown = drawdown;
    });

    return {
      totalReturn: totalReturn.toFixed(2),
      winRate: winRate.toFixed(1),
      sharpeRatio: sharpeRatio.toFixed(2),
      maxDrawdown: maxDrawdown.toFixed(2),
      totalTrades: trades.length,
      avgPnL: (totalPnL / trades.length).toFixed(0),
      profitFactor: this.calculateProfitFactor(trades),
    };
  }

  calculateProfitFactor(trades) {
    const grossProfit = trades.filter(t => t.pnl > 0).reduce((sum, t) => sum + t.pnl, 0);
    const grossLoss = Math.abs(trades.filter(t => t.pnl < 0).reduce((sum, t) => sum + t.pnl, 0));
    return grossLoss > 0 ? (grossProfit / grossLoss).toFixed(2) : 'N/A';
  }

  calculateMonthlyReturns(results) {
    const monthly = {};
    results.trades.forEach(trade => {
      const month = trade.exitDate.substring(0, 7); // YYYY-MM
      if (!monthly[month]) monthly[month] = { pnl: 0, trades: 0 };
      monthly[month].pnl += trade.pnl;
      monthly[month].trades++;
    });
    return monthly;
  }
}

export default BacktestingEngine;
